/**
 * Dismissal memory for the "Gig Boost ended" notice.
 *
 * The banner is per-event and must never reappear once dismissed, so the
 * dismissed assignment ids live in localStorage rather than component state.
 * Visiting the Assignments page counts as seeing it and dismisses it too.
 *
 * All reads are SSR-safe and failure-tolerant: private browsing modes throw on
 * localStorage access, and a notice that fails to hide is far better than a
 * page that fails to render.
 */

const STORAGE_KEY = 'assignmentEndedNoticesSeen';

// Only surface assignments that ended recently — an old one resurfacing
// months later would be noise, not news.
export const NOTICE_MAX_AGE_DAYS = 30;

const ENDED_STATUSES = ['revoked', 'expired'];

export const isEndedAssignment = assignment =>
  ENDED_STATUSES.includes(assignment?.status);

export const readSeenNoticeIds = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

export const markNoticesSeen = ids => {
  if (typeof window === 'undefined' || !ids?.length) return readSeenNoticeIds();
  try {
    const next = Array.from(new Set([...readSeenNoticeIds(), ...ids]))
      // Keep the list from growing without bound for long-lived accounts.
      .slice(-200);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch (e) {
    return readSeenNoticeIds();
  }
};

/**
 * The single ended assignment worth showing a notice for: most recently
 * ended, within the age window, not already seen. Returns null when there is
 * nothing to announce.
 */
export const pickNotice = (assignments, seenIds = []) => {
  const cutoff = Date.now() - NOTICE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
  const candidates = (assignments || [])
    .filter(a => a?.id && isEndedAssignment(a) && !seenIds.includes(a.id))
    .filter(a => {
      const at = new Date(a.updatedAt || 0).getTime();
      return Number.isFinite(at) && at >= cutoff;
    })
    .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  return candidates[0] || null;
};
